import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/features/auth/services/authService';
import { clearRememberMe } from '@/features/auth/utils/rememberMeStorage';
import { apiService } from '@/services/apiService';
import { playClickSound } from '@/utils/soundUtils';
import { useTranslation } from 'react-i18next';

/** Figma node 0-5066: logout icon 24×28, black */
function LogoutIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="28" viewBox="0 0 24 28" fill="none" aria-hidden>
      <path d="M5 22.2861C4.45 22.2861 3.97917 22.0996 3.5875 21.7266C3.19583 21.3536 3 20.9052 3 20.3814V7.04804C3 6.52423 3.19583 6.07582 3.5875 5.7028C3.97917 5.32978 4.45 5.14328 5 5.14328H12V7.04804H5V20.3814H12V22.2861H5ZM16 18.4766L14.625 17.0957L17.175 14.6671H9V12.7623H17.175L14.625 10.3338L16 8.9528L21 13.7147L16 18.4766Z" fill="black" />
    </svg>
  );
}

interface AuthenticatedFooterProps {
  className?: string;
  /** When true, footer fits in 80px height (e.g. wallet page fixed layout) */
  compact?: boolean;
}

/**
 * Footer component for authenticated pages (after login)
 * Based on Figma design (node-id=0-1922):
 * - Footer: height 67px, padding 16 top/bottom, max-width 1152, border top 1px 20%
 * When compact: fits in 80px (smaller padding and button).
 */
const AuthenticatedFooter: React.FC<AuthenticatedFooterProps> = ({ className = '', compact = false }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = useCallback(async () => {
    playClickSound();

    try {
      await authService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
    // Guarantee: clear token, clear remember, clear cache, then go home
    apiService.clearTokens();
    apiService.clearUserCache();
    clearRememberMe();
    navigate('/', { replace: true });
  }, [navigate]);

  return (
    <footer
      role="contentinfo"
      className={`w-full min-w-0 block ${compact ? 'h-full flex flex-col justify-center' : ''} ${className}`}
      style={{
        ...(compact ? { height: '100%', minHeight: 0 } : { minHeight: '67px' }),
        paddingTop: compact ? 10 : 16,
        paddingBottom: compact ? 10 : 16,
        paddingLeft: compact ? 'clamp(10px, 2.5vw, 20px)' : 'clamp(16px, 4vw, 32px)',
        paddingRight: compact ? 'clamp(10px, 2.5vw, 20px)' : 'clamp(16px, 4vw, 32px)',
        borderTop: '1px solid rgba(255, 255, 255, 0.2)',
        background: 'rgba(5, 11, 24, 0.95)',
        boxSizing: 'border-box',
      }}
    >
        <div
        className={`max-w-[1152px] mx-auto w-full min-w-0 flex flex-row items-center justify-between ${compact ? 'gap-1.5 sm:gap-2' : 'gap-2 sm:gap-4'}`}
        style={{ boxSizing: 'border-box' }}
      >
        {/* Left side - SECURE PORTAL + encrypted message */}
        <div className={`flex items-center flex-1 min-w-0 text-left overflow-hidden ${compact ? 'gap-1.5 sm:gap-2' : 'gap-2 sm:gap-3'}`}>
          <span
            className={`flex-shrink-0 rounded-full ${compact ? 'w-1.5 h-1.5 sm:w-2 sm:h-2' : 'w-2 h-2 sm:w-3 sm:h-3'}`}
            style={{
              background: 'var(--active, #10B981)',
              boxShadow: '0 0 15px 0 rgba(16, 185, 129, 0.80)',
            }}
            aria-hidden
          />
          <div className="min-w-0 flex-1">
            <p
              className="uppercase mb-0 truncate leading-tight"
              style={{
                color: 'rgba(255, 255, 255, 0.40)',
                fontFamily: 'Inter, sans-serif',
                fontSize: compact ? 'clamp(7px, 1.6vw, 8px)' : 'clamp(8px, 2vw, 10px)',
                fontWeight: 900,
                lineHeight: 1.2,
              }}
            >
              {t('authenticatedFooter.securePortal')}
            </p>
            <p
              className="hidden sm:block self-stretch leading-tight"
              style={{
                color: 'rgba(255, 255, 255, 0.60)',
                fontFamily: 'Inter, sans-serif',
                fontSize: compact ? 11 : 14,
                fontWeight: 400,
                lineHeight: compact ? 1.35 : 1.4,
              }}
            >
              {t('authenticatedFooter.encryptedMessage')}
            </p>
          </div>
        </div>

        {/* Log out button */}
        <button
          type="button"
          onClick={handleLogout}
          data-tour-id="post-login-tour.logout"
          className={`flex flex-shrink-0 items-center justify-center gap-1 sm:gap-1.5 uppercase transition-all duration-200 active:scale-95 whitespace-nowrap rounded-lg sm:rounded-xl ${
            compact
              ? 'h-7 sm:h-8 px-2 sm:px-3 py-1 min-w-[72px] sm:min-w-[100px]'
              : 'h-8 sm:h-[60px] px-2 sm:px-6 py-1.5 sm:py-0 min-w-[100px] sm:min-w-0 sm:w-[275px]'
          }`}
          style={{
            background: 'var(--Button---yellow, linear-gradient(180deg, #FFED4A 0%, #FC0 100%))',
            color: 'var(--Text---black, #000)',
            textAlign: 'center',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.50)',
            fontFamily: 'Inter, sans-serif',
            fontSize: compact ? 'clamp(8px, 1.8vw, 12px)' : 'clamp(10px, 2.5vw, 20px)',
            fontWeight: 900,
            lineHeight: '1.2',
            letterSpacing: '0.5px',
            boxShadow: '0 0 15px 0 rgba(255, 255, 255, 0.60) inset, 0 0 20px 0 rgba(255, 204, 0, 0.40)',
          }}
          aria-label={t('authenticatedFooter.logoutAriaLabel')}
        >
          <span className={compact ? 'text-[8px] sm:text-[10px] lg:text-xs' : 'text-[10px] sm:text-base lg:text-xl'}>
            {t('authenticatedFooter.logoutNow')}
          </span>
          <span
            className={`flex flex-shrink-0 items-center justify-center ${compact ? 'w-2 h-2 sm:w-3 sm:h-3.5' : 'w-3 h-3 sm:w-6 sm:h-7'}`}
            aria-hidden
          >
            <LogoutIcon />
          </span>
        </button>
      </div>
    </footer>
  );
};

export default AuthenticatedFooter;
